import React, { useState } from 'react';
import { Modal, Input, Textarea, Body, Flex } from '@cognite/cogs.js';
import { LabelListEdit } from 'components/LabelList/LabelListEdit';

type NewCollectionModalProps = {
  visible: boolean;
  onClose: VoidFunction;
  onCollectionCreated: (params: {
    name: string;
    description: string;
    labels: string[];
  }) => Promise<void>;
  digitalTwinId: string;
};

export const NewCollectionModal = ({
  visible,
  onClose,
  onCollectionCreated,
}: NewCollectionModalProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [labels, setLabels] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [nameError, setNameError] = useState<string | undefined>();

  const resetForm = () => {
    setName('');
    setDescription('');
    setLabels([]);
    setNameError(undefined);
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setNameError('Please enter a collection name');
      return;
    }

    setSubmitting(true);
    try {
      await onCollectionCreated({
        name: name.trim(),
        description,
        labels,
      });
      resetForm();
      onClose();
    } catch (err: unknown) {
      setNameError(
        err instanceof Error ? err.message : 'Failed to create collection'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Create Consistency Collection"
      visible={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      okDisabled={submitting}
      okText="Create"
      cancelText="Cancel"
      size="medium"
    >
      <Flex direction="column" gap={16} style={{ marginTop: 8 }}>
        <Input
          fullWidth
          label="Name"
          placeholder="e.g., Asset-WorkOrder Validation"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (nameError) setNameError(undefined);
          }}
          error={nameError}
        />

        <Textarea
          fullWidth
          label="Description"
          placeholder="Describe what this consistency check validates..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        <Flex direction="column" gap={6}>
          <Body size="medium" strong>
            Labels
          </Body>
          <LabelListEdit
            labels={labels}
            onChange={setLabels}
            disabled={submitting}
          />
        </Flex>
      </Flex>
    </Modal>
  );
};
